/**
 * Minimal solar position helpers (adapted from vasturiano/solar-calculator
 * formulas used by the globe.gl day-night example). No network, no dep —
 * lives with ambiance so time-of-day logic stays in one module.
 */

/** Julian centuries since J2000.0 for a unix-ms timestamp. */
export function solarCentury(ms: number): number {
  return (ms - Date.UTC(2000, 0, 1, 12)) / 864e5 / 36525;
}

/** Equation of time in minutes. */
export function equationOfTime(t: number): number {
  const epsilon = obliquity(t);
  const l0 = (280.46646 + t * (36000.76983 + t * 0.0003032)) % 360;
  const e = 0.016708634 - t * (0.000042037 + 0.0000001267 * t);
  const m = (357.52911 + t * (35999.05029 - 0.0001537 * t)) % 360;
  const y = Math.tan(toRad(epsilon) / 2) ** 2;
  const sin2l0 = Math.sin(2 * toRad(l0));
  const sinm = Math.sin(toRad(m));
  const cos2l0 = Math.cos(2 * toRad(l0));
  const sin4l0 = Math.sin(4 * toRad(l0));
  const sin2m = Math.sin(2 * toRad(m));
  const eot =
    y * sin2l0 -
    2 * e * sinm +
    4 * e * y * sinm * cos2l0 -
    0.5 * y * y * sin4l0 -
    1.25 * e * e * sin2m;
  return toDeg(eot) * 4;
}

/** Solar declination in degrees. */
export function declination(t: number): number {
  const e = obliquity(t);
  const l0 = (280.46646 + t * (36000.76983 + t * 0.0003032)) % 360;
  const m = (357.52911 + t * (35999.05029 - 0.0001537 * t)) % 360;
  const c =
    Math.sin(toRad(m)) * (1.914602 - t * (0.004817 + 0.000014 * t)) +
    Math.sin(toRad(2 * m)) * (0.019993 - 0.000101 * t) +
    Math.sin(toRad(3 * m)) * 0.000289;
  const trueLong = l0 + c;
  return toDeg(Math.asin(Math.sin(toRad(e)) * Math.sin(toRad(trueLong))));
}

function obliquity(t: number): number {
  return 23 + (26 + (21.448 - t * (46.815 + t * (0.00059 - t * 0.001813))) / 60) / 60;
}

function toRad(d: number): number {
  return (d * Math.PI) / 180;
}

function toDeg(r: number): number {
  return (r * 180) / Math.PI;
}

/**
 * Subsolar [lng, lat] at a simulated unix-ms time.
 * Same shape as the globe.gl day-night example's `sunPosAt`.
 */
export function sunPositionAt(ms: number): { lng: number; lat: number } {
  const dayStart = new Date(ms);
  dayStart.setUTCHours(0, 0, 0, 0);
  const t = solarCentury(ms);
  const longitude = ((+dayStart - ms) / 864e5) * 360 - 180;
  return {
    lng: longitude - equationOfTime(t) / 4,
    lat: declination(t),
  };
}
